import * as React from 'react';
import cx from 'classnames';
import px from 'prop-types';
import { v4 } from 'uuid';
import { ProductRating, ProductBadge } from 'Common/components/product';
import { ToolTip } from 'Common/components/ui';
import { useLinkHintText } from 'Common/components/ui/LinkHint';
import { useViewport } from 'Common/hooks';
import { Url } from 'Common/utils';
import { getLinkRel } from 'Common/utils/LinkAccessibility';
import { LinkItem as LinkItemType } from 'Common/types';

export default function CatalogCard({
    className,
    defaultImage = null,
    hoverEffect = false,
    id: initId = v4(),
    marketingBadges,
    onSelectProduct,
    price = '',
    productLinks = [],
    productUrl = null,
    rating,
    reducedPrice = '',
    reviewProviderEntityId,
    secondaryImage = null,
    title = 'N/A',
}) {
    const id = React.useMemo(() => initId, [initId]);
    const [cardImageUrl, setCardImageUrl] = React.useState(defaultImage);
    const cardRef = React.useRef();
    const viewport = useViewport();

    React.useEffect(() => {
        setCardImageUrl(defaultImage);
    }, [defaultImage]);

    const badges = React.useMemo(
        () => (marketingBadges?.length && viewport.is.lt('md') ? [marketingBadges[0]] : marketingBadges),
        [marketingBadges, viewport]
    );

    const onImageOver = React.useCallback(() => {
        if (secondaryImage && viewport.is.gt('md')) setCardImageUrl(secondaryImage);
    }, [setCardImageUrl, secondaryImage, viewport]);

    const onImageOut = React.useCallback(() => {
        if (defaultImage && viewport.is.gt('md')) setCardImageUrl(defaultImage);
    }, [setCardImageUrl, defaultImage, viewport]);

    const productLink = React.useMemo(
        () => (productUrl && Url.isAbsolute(productUrl) ? Url.create(productUrl) : null),
        [productUrl]
    );

    const productUrlIsForSameSite = React.useMemo(() => {
        if (productLink === null) return true;
        return productLink.host === location.host;
    }, [productLink]);

    const anchorTarget = React.useMemo(() => (productUrlIsForSameSite ? '_self' : '_blank'), [productUrlIsForSameSite]);

    const anchorRel = React.useMemo(() => getLinkRel(anchorTarget) || undefined, [anchorTarget]);

    // The card is named by its title element, which would suppress any hidden text nested inside the
    // anchor, so the hint is exposed as its own element and added to the labelledby id list.
    const hintText = useLinkHintText(productLink?.href, anchorTarget);

    const addBottomMargin = React.useMemo(() => {
        return reviewProviderEntityId == ''
    }, [reviewProviderEntityId])

    const handleSelectProduct = React.useCallback(
        (e) => {
            if (onSelectProduct) {
                e.stopPropagation();
                e.preventDefault();
                onSelectProduct(e, cardRef);
            }
        },
        [onSelectProduct]
    );

    const handleProductLinkClick = React.useCallback(
        (action) => (e) => {
            e.stopPropagation();
            e.preventDefault();
            if (typeof action === 'string') {
                window.location.href = action;
            } else if (action?.href) {
                if (action.target === '_blank') {
                    window.open(action.href, action.target, 'noopener,noreferrer');
                } else {
                    window.location.href = action.href;
                }
            } else {
                action(e, cardRef);
            }
        },
        []
    );

    return (
        <a
            ref={cardRef}
            id={id}
            href={productLink?.href}
            target={anchorTarget}
            rel={anchorRel}
            className={cx('CatalogCard', className)}
            onMouseOver={hoverEffect ? onImageOver : null}
            onMouseOut={hoverEffect ? onImageOut : null}
            onClick={onSelectProduct ? handleSelectProduct : null}
            aria-labelledby={hintText ? `${id}-title ${id}-hint` : `${id}-title`}
        >
            {hintText ? (
                <span id={`${id}-hint`} className="visually-hidden">
                    {hintText}
                </span>
            ) : null}
            <img src={cardImageUrl} aria-hidden="true" alt="" />
            <div className="CatalogCard__content">
                <div className={`CatalogCard__badges ${addBottomMargin ? 'mb-3' : ''}`}>
                    {badges?.length ? (
                        <>
                            <ProductBadge badges={badges} />
                        </>
                    ) : null}
                </div>
                {reviewProviderEntityId != '' ? (
                    <ProductRating rating={rating} reviewProviderEntityId={reviewProviderEntityId} slimView />
                ) : null}
                <div id={`${id}-title`} className="subheader">
                    {title}
                </div>
                <div className="CatalogCard__prices">
                    <div className={cx('CatalogCard__price', { strike: reducedPrice })}>{price}</div>
                    {reducedPrice ? <div className="CatalogCard__reduced-price">{reducedPrice}</div> : null}
                </div>
                <ul className="CatalogCard__links">
                    {productLinks?.map(({ label, key, icon, action, disabled, tip, tipDisabled = !!tip } = {}) => (
                        <li key={key}>
                            <ToolTip tip={tip} disabled={tipDisabled}>
                                <div
                                    className={cx('paragraph-1', { disabled })}
                                    onClick={disabled || !action ? null : handleProductLinkClick(action)}
                                >
                                    {icon ? <i className={cx(icon, 'me-1')} /> : null}
                                    <span>{label}</span>
                                </div>
                            </ToolTip>
                        </li>
                    ))}
                </ul>
            </div>
        </a>
    );
}

CatalogCard.propTypes = {
    className: px.string,
    defaultImage: px.string,
    id: px.string,
    hoverEffect: px.bool,
    marketingBadges: px.arrayOf(px.string),
    onSelectProduct: px.func,
    price: px.string,
    productLinks: px.arrayOf(
        px.shape({
            label: px.node,
            icon: px.string,
            action: px.oneOfType([px.string, px.func, LinkItemType]),
        })
    ),
    productUrl: px.string,
    rating: px.number,
    reducedPrice: px.string,
    reviewProviderEntityId: px.string,
    secondaryImage: px.string,
    title: px.string,
};
